import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import ConfirmButtons from '@zeus/core/buttons/ConfirmButtons';
import {
  fetchAllOrganizations,
  fetchOrganizationBySlug,
  createOrganization,
  updateOrganization,
} from '_/api/organizations/organizations.api';
import { IOrgRouteParams } from '_/common/routes/routes.types';
import guidValidator from '_/common/utils/validation/guidValidator';
import { useAuth } from '_/contexts/AuthContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { ICreateOrganizationDTO, IUpdateOrganizationDTO } from '_/types/organizations/organization.types';
import createSuggestedSlug from '_/utils/createSuggestedSlug';
import nameValidator from '_/utils/validation/nameValidator';
import slugValidator from '_/utils/validation/slugValidator';
import { Form, Input, Alert, Spin, notification, Modal, Button } from 'antd';
import React, { ReactElement, useEffect, useState, useMemo, useCallback } from 'react';

import classes from './NewOrganizationModal.module.css';
import VerifyLegacyPlatformIDCaution from './VerifyLegacyPlatformIDCaution';

interface FormValues {
  name: string;
  slug: string;
  legacyPlatformId: string;
}

interface NewOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  orgSlug?: string; // Optional org slug for edit mode
}

const NewOrganizationModal = ({ open, onClose, orgSlug: propOrgSlug }: NewOrganizationModalProps): ReactElement => {
  const params = useParams({ strict: false }) as Partial<IOrgRouteParams>;
  // Use prop orgSlug if provided, otherwise fall back to route params (for backward compatibility)
  const orgSlug = propOrgSlug || params.orgSlug;
  const history = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { setBreadcrumbs, setActionButtons } = useTopHeader();

  const isEditMode = !!orgSlug;

  // Fetch organization data if in edit mode
  const { data: selectedOrg, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization', orgSlug],
    queryFn: () => fetchOrganizationBySlug(orgSlug!),
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch all organizations for validation
  const { data: allOrganizations = [] } = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: fetchAllOrganizations,
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (org: ICreateOrganizationDTO) => createOrganization(org),
    onSuccess: async () => {
      await Promise.all([
        void queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] }),
        void queryClient.invalidateQueries({ queryKey: ['organizations', 'my'] }),
      ]);

      notification.success({
        message: 'Organization created',
      });

      // Close modal and reset form
      onClose();
      form.resetFields();
      setFormTouched(false);
      setSlugManuallyChanged(false);
    },
    onError: (error: any) => {
      console.error('Error creating organization:', error);
      notification.error({
        message: error?.message || 'Error creating organization',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (org: IUpdateOrganizationDTO) => updateOrganization(selectedOrg?.id || '', org),
    onSuccess: async (data) => {
      await Promise.all([
        void queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] }),
        void queryClient.invalidateQueries({ queryKey: ['organizations', 'my'] }),
        data?.slug ? void queryClient.invalidateQueries({ queryKey: ['organization', data.slug] }) : Promise.resolve(),
      ]);

      notification.success({
        message: 'Organization updated',
      });

      // Close modal if in modal mode (propOrgSlug provided)
      if (propOrgSlug) {
        onClose();
        form.resetFields();
        setFormTouched(false);
        setSlugManuallyChanged(false);
      }
    },
    onError: (error: any) => {
      console.error('Error updating organization:', error);
      notification.error({
        message: error?.message || 'Error updating organization',
      });
    },
  });

  const [formTouched, setFormTouched] = useState(false);
  const [slugManuallyChanged, setSlugManuallyChanged] = useState(false);

  // Other organizations for validation (exclude current org in edit mode)
  const otherOrganizations = useMemo(
    () => (isEditMode && selectedOrg ? allOrganizations.filter((o) => o.id !== selectedOrg.id) : allOrganizations),
    [allOrganizations, isEditMode, selectedOrg],
  );

  const handleBackClick = useCallback(() => {
    const url = isEditMode ? `/orgs/${orgSlug}/settings` : '/orgs';
    void history({ to: url });
  }, [isEditMode, orgSlug, history]);

  // Set up header with breadcrumbs (only for edit mode, modal doesn't need breadcrumbs)
  useEffect(() => {
    if (!isEditMode || !selectedOrg) return;

    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrg.name, href: `/orgs/${selectedOrg.slug}` },
      { title: 'Settings', href: `/orgs/${selectedOrg.slug}/settings` },
      { title: 'Edit Organization' },
    ]);
    setActionButtons([
      {
        onClick: handleBackClick,
        icon: <ArrowLeftOutlined />,
        label: 'Back',
      },
    ]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [setBreadcrumbs, setActionButtons, isEditMode, selectedOrg, handleBackClick]);

  // Initialize form values when editing
  useEffect(() => {
    if (isEditMode && selectedOrg) {
      form.setFieldsValue({
        name: selectedOrg.name,
        slug: selectedOrg.slug,
        legacyPlatformId: selectedOrg.legacyPlatformId || '',
      });
      setSlugManuallyChanged(true); // Don't auto-generate slug when editing
    }
  }, [isEditMode, selectedOrg, form]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    form.setFieldsValue({ name: name });
    setFormTouched(true);

    // Auto-generate slug when creating and slug hasn't been manually changed
    if (!isEditMode && !slugManuallyChanged) {
      form.setFieldsValue({ slug: createSuggestedSlug(name) });
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = e.target.value;
    form.setFieldsValue({ slug: slug });
    setFormTouched(true);
    setSlugManuallyChanged(true);
  };

  const handleFieldChange = () => {
    setFormTouched(true);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      if (isEditMode && selectedOrg) {
        // Build update data with all editable fields
        const updateData: any = {
          name: values.name,
          legacyPlatformId: values.legacyPlatformId || undefined,
        };

        await updateMutation.mutateAsync(updateData);

        // Only navigate if not in modal mode (when using route-based edit)
        if (!propOrgSlug) {
          void history({ to: `/orgs/${selectedOrg.slug}/settings` });
          setFormTouched(false);
        }
      } else {
        // Create new organization - mutation will handle closing modal
        await createMutation.mutateAsync({
          name: values.name,
          slug: values.slug,
          legacyPlatformId: values.legacyPlatformId,
        });
      }
    } catch (error) {
      console.error('Error saving organization:', error);
    }
  };

  // Custom validation function for Ant Design
  const validateField = async (fieldName: keyof FormValues, value: string) => {
    const entityName = 'An Organization';

    try {
      if (fieldName === 'name') {
        const schema = nameValidator(otherOrganizations, entityName);
        await schema.validate(value);
      } else if (fieldName === 'slug' && !isEditMode) {
        const schema = slugValidator(otherOrganizations, entityName);
        await schema.validate(value);
      } else if (fieldName === 'legacyPlatformId') {
        const schema = guidValidator('Legacy Platform ID');
        await schema.validate(value);
      }
    } catch (err: any) {
      throw new Error(err.message);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveErrors = createMutation.error || updateMutation.error;

  // Form content
  const formContent = (
    <>
      {saveErrors && (
        <Alert
          title={(saveErrors as any)?.message || 'An error occurred while saving'}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFieldChange}
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
      >
        <div>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Name is required' },
              {
                validator: async (_, value) => {
                  if (value) {
                    await validateField('name', value);
                  }
                },
              },
            ]}
            className={classes.minHeight}
          >
            <Input onChange={handleNameChange} />
          </Form.Item>

          <Form.Item
            label="Slug / ID - CANNOT BE CHANGED AFTER CREATION"
            name="slug"
            rules={
              !isEditMode
                ? [
                    { required: true, message: 'Slug is required' },
                    {
                      validator: async (_, value) => {
                        if (value) {
                          await validateField('slug', value);
                        }
                      },
                    },
                  ]
                : []
            }
            className={classes.minHeight}
          >
            <Input onChange={handleSlugChange} disabled={isEditMode} />
          </Form.Item>

          <Form.Item
            label="Legacy Platform ID"
            name="legacyPlatformId"
            rules={[
              { required: true, message: 'Legacy Platform ID is required' },
              {
                validator: async (_, value) => {
                  if (value) {
                    await validateField('legacyPlatformId', value);
                  }
                },
              },
            ]}
            className={classes.minHeight}
          >
            <Input />
          </Form.Item>
          <VerifyLegacyPlatformIDCaution />
        </div>

        {/* Show modal buttons when in modal mode (create or edit via prop), otherwise show page buttons */}
        {!isEditMode || propOrgSlug ? (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={() => form.submit()} loading={isSaving} disabled={!formTouched}>
              Save
            </Button>
          </div>
        ) : (
          <ConfirmButtons confirmText="Save" disabled={!formTouched} onCancel={handleBackClick} onConfirm={() => form.submit()} />
        )}
      </Form>
    </>
  );

  // If edit mode via route (not modal), render as a page
  if (isEditMode && !propOrgSlug) {
    // Show spinner while loading organization data in edit mode
    if (isLoadingOrg || isSaving) {
      return (
        <div className={classes.progressContainer}>
          <Spin size="large" />
        </div>
      );
    }

    return <div style={{ maxWidth: 800, margin: '0 auto' }}>{formContent}</div>;
  }

  // Otherwise, render as a modal (create mode or edit via modal)
  const modalTitle = isEditMode ? 'Edit Organization' : 'New Organization';

  return (
    <Modal title={modalTitle} open={open} onCancel={onClose} footer={null} width={600} destroyOnHidden confirmLoading={isSaving}>
      {isLoadingOrg ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        formContent
      )}
    </Modal>
  );
};

export default NewOrganizationModal;
